import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Spin } from "antd";
import { Col, Row } from "react-bootstrap";
import { FormikProvider, useFormik } from "formik";
import CustomInput from "../../../CustomControls/CustomInput";
import CustomSelect from "../../../CustomControls/CustomSelect";
import { post } from "../../../helpers/api_helper";
import { errorMessage, formatDateTime, warningMessage } from "../CommonFunctions";
import {
  POST_MachineIntegrationGetMachineProfile,
  POST_MachineIntegrationGetMachineStatusList,
  POST_MachineIntegrationGetPresetList,
  POST_MachineIntegrationSaveMachineProfile,
} from "../../../helpers/url_helper";

// Machines > Edit > "Automatic results from the instrument".
// Written for lab staff: no technical words on the main screen. Behind the scenes it still saves the same profile
// (result source, computer name, preset code, overrides); presets are read-only here (maintained by Appegic admins).

const RESULT_SOURCE_MANUAL = 1;
const RESULT_SOURCE_INSTRUMENT_PC = 2;

const parseObject = (text) => {
  try {
    const o = JSON.parse(text);
    return o && typeof o === "object" && !Array.isArray(o) ? o : {};
  } catch {
    return {};
  }
};

const getPath = (obj, path) =>
  path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);

// returns a copy of obj with path set; "" removes the key (and an emptied section), so only real overrides are saved
const setPath = (obj, path, value) => {
  const clone = JSON.parse(JSON.stringify(obj || {}));
  const keys = path.split(".");
  let cur = clone;
  keys.slice(0, -1).forEach((k) => {
    if (typeof cur[k] !== "object" || cur[k] === null) cur[k] = {};
    cur = cur[k];
  });
  const last = keys[keys.length - 1];
  if (value === "" || value === undefined || value === null) delete cur[last];
  else cur[last] = value;
  const top = keys[0];
  if (
    keys.length > 1 &&
    typeof clone[top] === "object" &&
    Object.keys(clone[top]).length === 0
  )
    delete clone[top];
  return clone;
};

// what is compared to know whether the screen differs from what is saved
const makeSnapshot = (profile, overrides) =>
  JSON.stringify({
    ResultSource: profile.ResultSource,
    AgentName: (profile.AgentName || "").trim(),
    PresetCode: profile.PresetCode || "",
    IsEnabled: profile.IsEnabled,
    ReadingValiditySeconds: profile.ReadingValiditySeconds,
    Overrides: overrides,
  });

// How long, after the instrument has sent a reading, the analyst can take it with the scale icon in Testing.
// The limit exists so an old value cannot end up in the wrong sample. The LIMS accepts 10 seconds to 10 minutes.
const READING_VALIDITY_OPTIONS = [
  { seconds: 30, label: "30 seconds" },
  { seconds: 60, label: "1 minute (standard)" },
  { seconds: 120, label: "2 minutes" },
  { seconds: 300, label: "5 minutes" },
  { seconds: 600, label: "10 minutes" },
];

const CONNECTION_TYPES = {
  Serial: "Cable to the computer (COM / USB port)",
  Folder: "Instrument saves a file on the computer",
  Tcp: "Over the network (analyzer sends its results to the computer)",
};

const TCP_MODE_OPTIONS = [
  { label: "The analyzer connects to the computer", value: "Listen" },
  { label: "The computer connects to the analyzer", value: "Connect" },
];

const MachineInstrumentConnection = forwardRef(({
  machineIdEncrypt,
  machineParameters = [],
}, ref) => {
  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));

  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    ResultSource: RESULT_SOURCE_MANUAL,
    AgentName: "",
    PresetCode: "",
    IsEnabled: true,
    ReadingValiditySeconds: 60,
  });
  const [overrides, setOverrides] = useState({});
  const [presets, setPresets] = useState([]);
  const [status, setStatus] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedDraft, setAdvancedDraft] = useState(null); // text being typed; null = show the current settings
  const [advancedError, setAdvancedError] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null); // what is stored in the LIMS; null until loaded
  const formikContext = useFormik({ initialValues: {}, onSubmit: () => {} });

  const isAutomatic = profile.ResultSource === RESULT_SOURCE_INSTRUMENT_PC;
  const preset = presets.find((p) => p.PresetCode === profile.PresetCode);
  const presetSettings = parseObject(preset?.SettingsJson);
  // what you set on the machine wins over the preset (same rule as on the server), e.g. Driver "Simulator" over a Serial preset
  const driver = overrides.Driver || preset?.Driver || "";
  const live =
    getPath(overrides, "Serial.Live") ?? getPath(presetSettings, "Serial.Live");
  const presetReading = getPath(presetSettings, "Serial.DefaultParameter") || "";
  // network analyzers: what is on the screen is what you set, else what the kind of instrument says
  const tcpMode = getPath(overrides, "Tcp.Mode") || getPath(presetSettings, "Tcp.Mode") || "Listen";
  const tcpPort = getPath(overrides, "Tcp.Port") || getPath(presetSettings, "Tcp.Port") || "";
  const readingNames = [
    ...new Set(machineParameters.map((n) => (n || "").trim()).filter(Boolean)),
  ];

  const loadStatus = () =>
    post(POST_MachineIntegrationGetMachineStatusList, {
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
      BranchIDEncrypted: AuthUser?.BranchIDEncrypt,
    })
      .then((response) => {
        setStatus(
          response?.List?.find(
            (x) => x.MachineIDEncrypted === machineIdEncrypt,
          ) ?? null,
        );
      })
      .catch((error) => console.log(error));

  const load = async () => {
    if (!machineIdEncrypt) return;
    setLoading(true);
    try {
      const p = await post(POST_MachineIntegrationGetMachineProfile, {
        CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
        BranchIDEncrypted: AuthUser?.BranchIDEncrypt,
        MachineIDEncrypted: machineIdEncrypt,
      });
      if (p?.IsSuccess) {
        const loadedProfile = {
          ResultSource: p.ResultSource || RESULT_SOURCE_MANUAL,
          AgentName: p.AgentName || "",
          PresetCode: p.PresetCode || "",
          IsEnabled: p.IsEnabled !== false,
          ReadingValiditySeconds: p.ReadingValiditySeconds || 60,
        };
        const loadedOverrides = parseObject(p.OverridesJson);
        setProfile(loadedProfile);
        setOverrides(loadedOverrides);
        setSavedSnapshot(makeSnapshot(loadedProfile, loadedOverrides));
      }

      // instruments of the same make as this machine are flagged IsSuggested and listed first
      const pl = await post(POST_MachineIntegrationGetPresetList, {
        Manufacturer: p?.ManufacturingCompany || "",
      });
      setPresets(pl?.IsSuccess ? pl.List : []);

      await loadStatus();
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [machineIdEncrypt]);

  const setOverride = (path, value) =>
    setOverrides((prev) => setPath(prev, path, value));

  const setNumberOverride = (path, text) => {
    if (text === "") return setOverride(path, "");
    const n = Number(text);
    if (Number.isFinite(n)) setOverride(path, n);
  };

  const changeInstrumentType = (code) => {
    const next = presets.find((p) => p.PresetCode === code);
    const nextDriver = next?.Driver || "";
    setProfile((prev) => ({ ...prev, PresetCode: code }));
    // settings of the other connection type would be meaningless (and misleading) for the new instrument type
    if (nextDriver && nextDriver !== driver) {
      setOverrides((prev) => {
        const clone = { ...prev };
        delete clone.Driver;
        delete clone.Serial;
        delete clone.Folder;
        delete clone.Tcp;
        return clone;
      });
    }
  };

  // true when the settings on the screen differ from what is stored
  const isDirty = () =>
    savedSnapshot !== null && makeSnapshot(profile, overrides) !== savedSnapshot;

  // Returns true when the settings are stored (or nothing needed storing), false when something is missing or the save failed.
  const save = async () => {
    if (advancedError) {
      warningMessage("The advanced settings are not valid. Fix them or close that box.");
      return false;
    }
    if (isAutomatic) {
      if (!profile.AgentName.trim()) {
        warningMessage("Instrument settings: enter the name of the computer connected to the instrument.");
        return false;
      }
      if (!profile.PresetCode && !["Serial", "Folder", "Tcp"].includes(overrides.Driver)) {
        warningMessage("Instrument settings: choose the kind of instrument, or how it is connected.");
        return false;
      }
      if (driver === "Serial" && !fieldValue("Serial.PortName")) {
        warningMessage("Instrument settings: enter the cable port the instrument is plugged into (for example COM4).");
        return false;
      }
      if (driver === "Simulator" && !fieldValue("Simulator.File")) {
        warningMessage("Instrument settings: enter the test file that stands in for the instrument.");
        return false;
      }
      if (
        (driver === "Serial" || driver === "Simulator") &&
        !fieldValue("Serial.DefaultParameter") &&
        !presetReading
      ) {
        warningMessage("Instrument settings: choose which reading the instrument sends (for example pH).");
        return false;
      }
      if (driver === "Tcp") {
        const port = Number(tcpPort);
        if (!Number.isInteger(port) || port < 1 || port > 65535) {
          warningMessage("Instrument settings: enter the network port number of the instrument connection (1 to 65535).");
          return false;
        }
        if (tcpMode === "Connect" && !String(fieldValue("Tcp.Host")).trim()) {
          warningMessage("Instrument settings: enter the network address of the analyzer.");
          return false;
        }
      }
      if (driver === "Folder" && !fieldValue("Folder.Path")) {
        warningMessage("Instrument settings: enter the folder where the instrument saves its files.");
        return false;
      }
    }

    setLoading(true);
    try {
      const response = await post(POST_MachineIntegrationSaveMachineProfile, {
        CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
        BranchIDEncrypted: AuthUser?.BranchIDEncrypt,
        MachineIDEncrypted: machineIdEncrypt,
        ResultSource: profile.ResultSource,
        AgentName: profile.AgentName.trim(),
        PresetCode: profile.PresetCode,
        OverridesJson: JSON.stringify(overrides),
        IsEnabled: profile.IsEnabled,
        ReadingValiditySeconds: profile.ReadingValiditySeconds,
        ModifiedByEncrypted: AuthUser?.ReferenceIDEncrypt
          ? AuthUser?.ReferenceIDEncrypt
          : "0",
      });
      if (response?.IsSuccess) {
        // no message here: this runs inside the machine's Submit, which shows the one confirmation for the whole page
        setSavedSnapshot(makeSnapshot(profile, overrides));
        await loadStatus();
        setLoading(false);
        return true;
      }
      warningMessage(response?.Message || "Could not save the instrument settings!");
    } catch (error) {
      console.log(error);
      errorMessage("Could not save the instrument settings!");
    }
    setLoading(false);
    return false;
  };

  // Called by the machine page's Submit button: saves the instrument settings too, but only when they were changed.
  // false = stop, the machine must not be submitted yet (a message has already been shown).
  useImperativeHandle(ref, () => ({
    saveIfNeeded: async () => (isDirty() ? save() : true),
  }));

  const onAdvancedChange = (text) => {
    setAdvancedDraft(text);
    try {
      const o = JSON.parse(text);
      if (o && typeof o === "object" && !Array.isArray(o)) {
        setOverrides(o);
        setAdvancedError(false);
      } else setAdvancedError(true);
    } catch {
      setAdvancedError(true);
    }
  };

  const fieldValue = (path) => {
    const v = getPath(overrides, path);
    return v === undefined || v === null ? "" : v;
  };

  // one sentence telling the user what the analysts will do in Testing
  const howItWorks = () => {
    if ((driver === "Serial" || driver === "Simulator") && live)
      return "In Testing, the analyst opens the sample and clicks the scale icon next to a result box. In the Raw Data Log Book, the analyst picks this equipment and the parameter, then clicks the scale icon next to a reading box. The box is filled with the instrument's current steady reading, and the analyst can still change it before saving.";
    if (driver === "Tcp")
      return "The analyzer sends the sample number with every result. In the Raw Data Log Book, when the analyst picks this equipment and the parameter, the reading boxes of the matching samples are filled automatically. In Testing, the analyst clicks \"Fetch Parameter Results\" after the analyzer has sent them. The reading names must match the Machine Parameters above.";
    if (driver === "Serial" || driver === "Simulator" || driver === "Folder")
      return "After the instrument has sent its results, the reading boxes of the matching samples are filled automatically in the Raw Data Log Book when the analyst picks this equipment and the parameter. In Testing, the analyst clicks \"Fetch Parameter Results\".";
    return "";
  };

  // The one status line shown at the top of the card: a label, a colour, and a sentence saying what it means.
  // It describes what is SAVED, so unsaved changes on the screen get their own "Not saved yet" state.
  const statusView = () => {
    if (savedSnapshot !== null && makeSnapshot(profile, overrides) !== savedSnapshot)
      return {
        cls: "bg-info text-dark",
        text: "Not saved yet",
        message: "You have changed these settings. They are saved when you click Submit at the bottom of the page.",
      };

    const computer = profile.AgentName;
    if (!isAutomatic)
      return {
        cls: "bg-secondary",
        text: "Automatic reading is off",
        message: "Analysts type the results for this machine themselves. To read this instrument automatically, switch it on below.",
      };
    if (!profile.IsEnabled)
      return {
        cls: "bg-secondary",
        text: "Paused",
        message: "Automatic reading is paused. Untick \"Pause\" below and click Submit to start it again.",
      };
    if (!status || status.ConnectionState === "Manual")
      return {
        cls: "bg-secondary",
        text: "Waiting",
        message: "Settings are saved. Waiting for the computer to pick them up (within 5 minutes).",
      };
    if (status.ConnectionState === "Connected")
      return {
        cls: "bg-success",
        text: "Working",
        message: `The computer "${computer}" is sending results. Last message: ${formatDateTime(status.LastHeartbeatOn)}. ${status.Detail || ""}`,
      };
    if (status.ConnectionState === "Error")
      return {
        cls: "bg-danger",
        text: "Problem",
        message: `The computer "${computer}" reports a problem: ${status.Detail}`,
      };
    return {
      cls: "bg-warning text-dark",
      text: "No response from the computer",
      message:
        (status.LastHeartbeatOn
          ? `No message from the computer "${computer}" since ${formatDateTime(status.LastHeartbeatOn)}. `
          : `The computer "${computer}" has not reported yet. `) +
        "Check that it is switched on, the Instrument Agent program is running on it, and the computer name above is typed exactly as on that computer.",
    };
  };

  const view = statusView();

  // ---- what is drawn -----------------------------------------------------------------------------------------------
  // Same controls and grid as the rest of the Machine page (CustomInput / CustomSelect, three columns). The values live in the
  // state above, so the shared controls get their `field` directly and need Formik only for its error display.
  const noForm = { touched: {}, errors: {}, setFieldValue: () => {} };
  const textField = (name, value, onChange) => ({ name, value, onChange, onBlur: () => {} });

  const presetOptions = [
    { label: "Other instrument (set up with PragyaLIMS support)", value: "" },
    ...presets.map((p) => ({ label: `${p.IsSuggested ? "\u2605 " : ""}${p.Title}`, value: p.PresetCode })),
  ];
  const connectionOptions = [
    { label: "-- choose --", value: "" },
    ...Object.entries(CONNECTION_TYPES).map(([value, label]) => ({ label, value })),
  ];
  const currentReading = fieldValue("Serial.DefaultParameter");
  const readingOptions = [
    { label: presetReading ? `Standard for this instrument (${presetReading})` : "-- choose --", value: "" },
    ...readingNames.map((name) => ({ label: name, value: name })),
    ...(currentReading && !readingNames.includes(currentReading)
      ? [{ label: `${currentReading} (not in this machine's list)`, value: currentReading }]
      : []),
  ];
  const validityOptions = [
    ...READING_VALIDITY_OPTIONS.map((o) => ({ label: o.label, value: o.seconds })),
    ...(READING_VALIDITY_OPTIONS.some((o) => o.seconds === profile.ReadingValiditySeconds)
      ? []
      : [{ label: `${profile.ReadingValiditySeconds} seconds`, value: profile.ReadingValiditySeconds }]),
  ];

  const hint = (text, cls = "text-muted") => <small className={`${cls} d-block mb-2`}>{text}</small>;

  return (
    <Spin size="large" spinning={loading} tip={"Loading..."} style={{ position: "fixed" }}>
      <FormikProvider value={formikContext}>
        <div
          className="card mb-2"
          style={{ height: "auto", border: "1px solid #E6E6E6" }}
          // this card sits inside the machine form: Enter in a text box must not submit the whole machine
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.tagName === "INPUT") e.preventDefault();
          }}
        >
          <div className="card-body p-2">
            <h5 className="mb-2">Automatic results from the instrument</h5>

            <div className="d-flex align-items-center flex-wrap border rounded p-2 mb-2">
              <strong className="me-2">Status:</strong>
              <span className={`badge ${view.cls} me-2`}>{view.text}</span>
              <span className="text-muted fs-13">{view.message}</span>
            </div>

            <div className="form-check form-switch mb-2">
              <input
                id="mic-automatic"
                type="checkbox"
                className="form-check-input"
                checked={isAutomatic}
                onChange={(e) =>
                  setProfile((prev) => ({
                    ...prev,
                    ResultSource: e.target.checked ? RESULT_SOURCE_INSTRUMENT_PC : RESULT_SOURCE_MANUAL,
                  }))
                }
              />
              <label htmlFor="mic-automatic" className="form-check-label">
                <strong>Take results from this instrument automatically</strong>
              </label>
              <small className="text-muted d-block">
                {isAutomatic
                  ? "A lab computer reads this instrument and sends the results to the LIMS."
                  : "Analysts type the results for this machine themselves."}
              </small>
            </div>

            {isAutomatic ? (
              <>
                <Row>
                  <Col lg={4}>
                    <CustomInput
                      field={textField("AgentName", profile.AgentName, (e) =>
                        setProfile((prev) => ({ ...prev, AgentName: e.target.value })),
                      )}
                      form={noForm}
                      label="Computer connected to the instrument"
                      placeholder="Computer name, e.g. LAB-PC-01"
                      maxLength={100}
                      required
                    />
                    {hint('On that computer: press the Windows key, type "About your PC" and read the "Device name".')}
                  </Col>

                  <Col lg={4}>
                    <CustomSelect
                      field={{ name: "PresetCode", value: profile.PresetCode }}
                      form={noForm}
                      label="Kind of instrument"
                      placeholder="Select kind of instrument"
                      options={presetOptions}
                      isClearable={false}
                      menuPosition="fixed"
                      onChange={(option) => changeInstrumentType(option?.value ?? "")}
                      required
                    />
                    {preset ? (
                      <small className={`d-block mb-2 ${preset.Status === "Documented" ? "text-success" : "text-warning"}`}>
                        {preset.Status === "Documented"
                          ? "Ready to use."
                          : "Test it with your instrument before you rely on it."}{" "}
                        <i className="fa fa-info-circle text-muted" title={preset.Notes}></i>
                      </small>
                    ) : (
                      hint("PragyaLIMS support gives you the settings. Choose how it is connected.")
                    )}
                  </Col>

                  {!preset ? (
                    <Col lg={4}>
                      <CustomSelect
                        field={{ name: "Driver", value: overrides.Driver || "" }}
                        form={noForm}
                        label="How is it connected?"
                        placeholder="Select"
                        options={connectionOptions}
                        isClearable={false}
                        menuPosition="fixed"
                        onChange={(option) => setOverride("Driver", option?.value ?? "")}
                        required
                      />
                    </Col>
                  ) : null}

                  {driver === "Serial" ? (
                    <Col lg={4}>
                      <CustomInput
                        field={textField("PortName", fieldValue("Serial.PortName"), (e) =>
                          setOverride("Serial.PortName", e.target.value.trim()),
                        )}
                        form={noForm}
                        label="Cable port"
                        placeholder="e.g. COM4"
                        maxLength={20}
                        required
                      />
                      {hint("On that computer: Device Manager > Ports (COM & LPT). Unplug and plug the cable to see which number appears.")}
                    </Col>
                  ) : null}

                  {driver === "Simulator" ? (
                    <Col lg={4}>
                      <CustomInput
                        field={textField("SimulatorFile", fieldValue("Simulator.File"), (e) =>
                          setOverride("Simulator.File", e.target.value),
                        )}
                        form={noForm}
                        label="Test file"
                        placeholder="e.g. C:\temp\fake-ph.txt"
                        maxLength={260}
                        required
                      />
                      {hint("Test mode, no instrument: each line added to this file counts as a reading.", "text-warning")}
                    </Col>
                  ) : null}

                  {driver === "Folder" ? (
                    <Col lg={4}>
                      <CustomInput
                        field={textField("FolderPath", fieldValue("Folder.Path"), (e) =>
                          setOverride("Folder.Path", e.target.value),
                        )}
                        form={noForm}
                        label="Export folder"
                        placeholder="e.g. C:\InstrumentExports"
                        maxLength={260}
                        required
                      />
                      {hint("The folder on that computer where the instrument software saves its results file (CSV).")}
                    </Col>
                  ) : null}

                  {driver === "Tcp" ? (
                    <>
                      <Col lg={4}>
                        <CustomSelect
                          field={{ name: "TcpMode", value: tcpMode }}
                          form={noForm}
                          label="Who connects?"
                          placeholder="Select"
                          options={TCP_MODE_OPTIONS}
                          isClearable={false}
                          menuPosition="fixed"
                          onChange={(option) => setOverride("Tcp.Mode", option?.value ?? "")}
                          required
                        />
                        {hint(
                          tcpMode === "Connect"
                            ? "Use this when the analyzer waits for the computer to connect to it."
                            : "Usual setting. In the analyzer's LIS (host) settings, enter this computer's address and the port below.",
                        )}
                      </Col>
                      {tcpMode === "Connect" ? (
                        <Col lg={4}>
                          <CustomInput
                            field={textField("TcpHost", fieldValue("Tcp.Host"), (e) =>
                              setOverride("Tcp.Host", e.target.value.trim()),
                            )}
                            form={noForm}
                            label="Analyzer network address"
                            placeholder="e.g. 192.168.1.50"
                            maxLength={100}
                            required
                          />
                          {hint("The address shown in the analyzer's network settings.")}
                        </Col>
                      ) : null}
                      <Col lg={4}>
                        <CustomInput
                          field={textField("TcpPort", tcpPort, (e) => setNumberOverride("Tcp.Port", e.target.value))}
                          form={noForm}
                          type="number"
                          label="Network port"
                          placeholder="e.g. 5000"
                          required
                        />
                        {hint(
                          tcpMode === "Connect"
                            ? "The port number from the analyzer's LIS settings."
                            : "Any free number you choose (for example 5000). Enter the same number on the analyzer, and allow it in the Windows firewall of that computer.",
                        )}
                      </Col>
                    </>
                  ) : null}

                  {driver === "Serial" || driver === "Simulator" ? (
                    <Col lg={4}>
                      <CustomSelect
                        field={{ name: "Reading", value: currentReading }}
                        form={noForm}
                        label="Reading sent by the instrument"
                        placeholder="Select reading"
                        options={readingOptions}
                        isClearable={false}
                        menuPosition="fixed"
                        isDisabled={readingNames.length === 0 && !presetReading}
                        onChange={(option) => setOverride("Serial.DefaultParameter", option?.value ?? "")}
                        required
                      />
                      {readingNames.length === 0
                        ? hint('Add the reading (for example pH) under "Machine Parameters" above first.', "text-warning")
                        : hint("Choose from this machine's Machine Parameters.")}
                    </Col>
                  ) : null}

                  {(driver === "Serial" || driver === "Simulator") && live ? (
                    <Col lg={4}>
                      <CustomSelect
                        field={{ name: "ReadingValiditySeconds", value: profile.ReadingValiditySeconds }}
                        form={noForm}
                        label="Reading can be taken for"
                        placeholder="Select time"
                        options={validityOptions}
                        isClearable={false}
                        menuPosition="fixed"
                        onChange={(option) =>
                          setProfile((prev) => ({ ...prev, ReadingValiditySeconds: Number(option?.value ?? 60) }))
                        }
                      />
                      {hint("In Testing, a reading can be taken only within this time of the instrument sending it.")}
                    </Col>
                  ) : null}
                </Row>

                {howItWorks() ? (
                  <small className="text-muted d-block mb-2">
                    <i className="fa fa-info-circle me-1"></i>
                    <strong>How it works: </strong>
                    {howItWorks()}
                  </small>
                ) : null}

                <div className="form-check mb-2">
                  <input
                    id="mic-pause"
                    type="checkbox"
                    className="form-check-input"
                    checked={!profile.IsEnabled}
                    onChange={(e) => setProfile((prev) => ({ ...prev, IsEnabled: !e.target.checked }))}
                  />
                  <label htmlFor="mic-pause" className="form-check-label">
                    Pause automatic reading (the settings are kept)
                  </label>
                </div>

                <div className="mb-2">
                  <a
                    href="#advanced"
                    className="text-muted"
                    onClick={(e) => {
                      e.preventDefault();
                      setAdvancedOpen((v) => !v);
                    }}
                  >
                    {advancedOpen ? "Hide advanced settings" : "Advanced settings (only if PragyaLIMS support asks)"}
                  </a>
                  {advancedOpen ? (
                    <div className="mt-2">
                      {driver === "Serial" ? (
                        <Row>
                          <Col lg={4}>
                            <CustomInput
                              field={textField("BaudRate", fieldValue("Serial.BaudRate"), (e) =>
                                setNumberOverride("Serial.BaudRate", e.target.value),
                              )}
                              form={noForm}
                              type="number"
                              label="Connection speed (baud rate)"
                              placeholder={
                                getPath(presetSettings, "Serial.BaudRate")
                                  ? `standard: ${getPath(presetSettings, "Serial.BaudRate")}`
                                  : ""
                              }
                            />
                            {hint("Must match the speed set on the instrument.")}
                          </Col>
                        </Row>
                      ) : null}
                      <textarea
                        className={`form-control form-control-sm ${advancedError ? "is-invalid" : ""}`}
                        rows={8}
                        spellCheck={false}
                        style={{ fontFamily: "monospace" }}
                        value={advancedDraft ?? JSON.stringify(overrides, null, 2)}
                        onChange={(e) => onAdvancedChange(e.target.value)}
                        onBlur={() => !advancedError && setAdvancedDraft(null)}
                      />
                      {hint("Technical settings for this machine only. Changing them wrongly can stop the instrument from sending results.")}
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}

            <button type="button" className="btn btn-outline-primary btn-sm" onClick={load}>
              <i className="fa-solid fa-refresh me-2"></i> Refresh status
            </button>
          </div>
        </div>
      </FormikProvider>
    </Spin>
  );
});

export default MachineInstrumentConnection;
